import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft, Home, Calendar, CalendarDays, Target, Trophy,
  Dice5, Gamepad2, Wallet, Users, Flame, DollarSign, Gift, Crown, CheckCircle2
} from 'lucide-react';
import api from '../../services/api';

const ICON_MAP = {
  Calendar, CalendarDays, Target, Trophy, Dice5, Gamepad2,
  Wallet, Users, Flame, DollarSign, Gift, Crown,
};

const KIND_CONFIG = {
  daily: {
    title: 'Daily Tasks',
    subtitle: 'Reset every day at midnight',
    headerIcon: Calendar,
    accent: 'var(--vl-page-tasks-accent, #10B981)',
    accentDark: '#059669',
    border: 'rgba(16,185,129,0.45)',
    cssClass: 'task-icon-pulse',
    emptyHint: 'No daily tasks set yet. Admins can add them in the panel.',
  },
  weekly: {
    title: 'Weekly Tasks',
    subtitle: 'Reset every Monday',
    headerIcon: CalendarDays,
    accent: '#F59E0B',
    accentDark: '#B45309',
    border: 'rgba(245,158,11,0.45)',
    cssClass: 'task-icon-flame',
    emptyHint: 'No weekly tasks set yet. Admins can add them in the panel.',
  },
};

export default function TasksPage({ kind = 'daily' }) {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const homePath = tenantSlug ? `/${tenantSlug}/app` : '/app';
  const explorePath = tenantSlug ? `/${tenantSlug}/app/explore` : '/app/explore';

  const cfg = KIND_CONFIG[kind] || KIND_CONFIG.daily;
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get(`/api/tasks?kind=${kind}`)
      .then((res) => { if (mounted) setTasks(res?.data?.tasks || []); })
      .catch(() => { /* show empty */ })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [kind]);

  const HeaderIcon = cfg.headerIcon;

  return (
    <div
      className="player-app min-h-screen pb-12"
      style={{ color: '#FFFFFF' }}
      data-testid={`tasks-page-${kind}`}
    >
      {/* Header */}
      <div
        className="sticky top-0 z-30 px-4 sm:px-6 py-4 flex items-center gap-3"
        style={{
          background: 'rgba(6,23,14,0.85)',
          backdropFilter: 'blur(18px) saturate(160%)',
          WebkitBackdropFilter: 'blur(18px) saturate(160%)',
          borderBottom: `1px solid ${cfg.border}`,
        }}
      >
        <button
          onClick={() => navigate(explorePath)}
          className="w-10 h-10 rounded-full flex items-center justify-center transition active:scale-95"
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: '#FFFFFF',
          }}
          data-testid={`tasks-${kind}-back`}
          aria-label="Back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1
            className="display font-extrabold text-xl sm:text-2xl flex items-center gap-2"
            style={{ color: '#FFFFFF' }}
          >
            <span
              className={`w-9 h-9 rounded-xl flex items-center justify-center ${cfg.cssClass}`}
              style={{
                background: `linear-gradient(135deg, ${cfg.accent}, ${cfg.accentDark})`,
                boxShadow: `0 6px 16px ${cfg.accent}88, inset 0 1px 0 rgba(255,255,255,0.45)`,
              }}
              aria-hidden
            >
              <HeaderIcon className="w-5 h-5 text-white" strokeWidth={2.6} />
            </span>
            {cfg.title}
          </h1>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
            {cfg.subtitle}
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <p className="text-center text-sm py-12" style={{ color: 'rgba(255,255,255,0.6)' }}>Loading…</p>
        ) : tasks.length === 0 ? (
          <div
            className="rounded-3xl p-8 text-center"
            style={{ background: 'rgba(255,255,255,0.04)', border: `1px dashed ${cfg.border}` }}
          >
            <Target className="w-10 h-10 mx-auto mb-3 opacity-40" style={{ color: cfg.accent }} />
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>{cfg.emptyHint}</p>
          </div>
        ) : (
          <ul className="space-y-3">
            {tasks.map((t) => {
              const Icon = ICON_MAP[t.icon] || Target;
              return (
                <li
                  key={t.id}
                  className="rounded-[28px] p-4 flex items-center gap-4 relative overflow-hidden"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${t.accent}55`,
                    boxShadow: `0 4px 12px ${t.accent}1F`,
                  }}
                  data-testid={`task-${t.id}`}
                >
                  <span
                    className="shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center"
                    style={{
                      background: `${t.accent}22`,
                      border: `1.5px solid ${t.accent}`,
                    }}
                    aria-hidden
                  >
                    <Icon className="w-6 h-6" style={{ color: t.accent }} strokeWidth={2.4} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p
                      className="display font-extrabold text-base sm:text-lg leading-tight"
                      style={{ color: '#FFFFFF' }}
                    >
                      {t.title}
                    </p>
                    <p
                      className="text-xs sm:text-sm leading-tight mt-0.5"
                      style={{ color: 'rgba(255,255,255,0.7)' }}
                    >
                      {t.description}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <span
                      className="display font-extrabold text-xs sm:text-sm px-3 py-1.5 rounded-2xl"
                      style={{ background: t.accent, color: '#0B0F0C' }}
                    >
                      {t.reward}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider flex items-center gap-1" style={{ color: 'rgba(255,255,255,0.5)' }}>
                      <CheckCircle2 className="w-3 h-3" />
                      Complete to claim
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
